import React from 'react';
import PropTypes from 'prop-types';
import ModalLayout from '../ModalLayout';
import BasicModalLayoutFooter from '../ModalLayout/BasicModalLayoutFooter';
import styles from './AddressInformation.scss';
import { GoogleMapsBasicClient } from 'wix-ui-core/dist/src/clients/GoogleMaps/GoogleMapsBasicClient';
import { AddressInput } from 'wix-ui-backoffice/AddressInput';
import { DeliveryTypes, EMPTY_ADDRESS, Modals } from '../../utils/constants';
import { formatAddress, getFormattedTimeGuarantee } from './addressInformationUtils';
import _ from 'lodash';
import Fader from '../Fader';
import FaderTitle from '../Fader/FaderTitle';
import AddressInformationBanner from './AddressInformationBanner';
import OnBoardingStepper from '../OnBoardingStepper';
import { BANNER_TYPES } from '../Banner';
import RestaurantHelper from '@wix/wixrest-utils/dist/openrest-helpers/Restaurant';
import { getDeliveryInfo, getMostExpensiveDeliveryFee } from '../../utils/CartUtils';
import CurrencyUtils from '@wix/wixrest-utils/dist/CurrencyUtils';

import {
  Layout,
  Cell,
  Thumbnail,
  TextButton,
  Text,
  FormField,
  Dropdown,
  Input,
  InputArea,
  Modal,
} from 'wix-style-react';

export const NEW_ADDRESS_ID = 'new-address';

export const DataHooks = {
  restaurant: 'address-info-restaurant',
  delivery: 'address-info-delivery-thumbnail',
  takeout: 'address-info-takeout-thumbnail',
  addressInput: 'address-info-address-input',
  dropdown: 'address-info-address-dropdown',
  selectBranch: 'address-info-select-branch',
  apt: 'address-info-apt',
  floor: 'address-info-floor',
  entrance: 'address-info-entrance',
  onArrival: 'address-info-on-arrival',
  comment: 'address-info-comment',
  restaurantAddress: 'address-info-restaurant-address',
  deliveryTime: 'address-info-delivery-time',
};

export default class AddressInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      newAddressMode: false,
      localAddress: props.address,
      localDeliveryType: props.deliveryType,
      contentOverflow: null,
    };
    this.faderRef = React.createRef();
    this.dropdownRef = React.createRef();
  }

  onRequestClose = () => {
    if (this.props.onRequestClose) {
      this.props.onRequestClose();
    } else {
      this.props.goBack();
    }
  };

  onFormSubmit = () => {
    const { localAddress, localDeliveryType } = this.state;
    const { updateDeliveryAddress, deliveryDetailsDone, updateDeliveryType, hideModal } = this.props;

    updateDeliveryType({ deliveryType: localDeliveryType });
    updateDeliveryAddress({ address: localAddress });
    deliveryDetailsDone();
    hideModal({ modal: Modals.ADDRESS_INFORMATION });
  };

  wrapIfNeeded = (renderedContent) => {
    const { isModal, isOpen } = this.props;

    if (isModal) {
      return (
        <Modal isOpen={isOpen} onRequestClose={this.onRequestClose} shouldCloseOnOverlayClick>
          {renderedContent}
        </Modal>
      );
    } else {
      return renderedContent;
    }
  };

  getAddressOptions = () => {
    const { addresses, t } = this.props;
    return [
      ...addresses.map((address) => ({ id: address.id, value: address.formatted })),
      {
        id: NEW_ADDRESS_ID,
        value: <TextButton>{t('phoneordering_customerinfo_ordertype_address_addnew_cta')}</TextButton>,
      },
    ];
  };

  onAddressDropdownSelect = ({ id }) => {
    if (id === NEW_ADDRESS_ID) {
      this.setState({ newAddressMode: true, localAddress: EMPTY_ADDRESS });
    } else {
      const address = this.props.addresses.find((a) => a.id === id);
      this.setState({ localAddress: address });
    }
  };

  onAddressInputSelect = ({ address }) => {
    const { localAddress } = this.state;
    this.setState({ contentOverflow: null }); // the options popup is closed-> we dont need to allow overflow anymore

    this.setState({
      localAddress: {
        ...localAddress,
        ...formatAddress(address),
      },
    });
  };

  onManualInput = (formatted) => {
    this.setState({ contentOverflow: null }); // the options popup is closed-> we dont need to allow overflow anymore

    this.setState({
      localAddress: {
        formatted,
      },
    });
  };

  handleThumbnailClick = (e, deliveryType) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ localDeliveryType: deliveryType });
  };

  onInputChange = (field, value) => {
    const { localAddress } = this.state;

    this.setState({
      localAddress: { ...localAddress, [field]: value },
    });
  };

  getInputProps = (field) => {
    const { localAddress } = this.state;
    return {
      value: localAddress[field] || '',
      onChange: (e) => this.onInputChange(field, e.target.value),
      dataHook: DataHooks[field],
    };
  };

  shouldShowFurtherInputs = (isDeliveryCurrentlyUnavailable) => {
    const { localAddress, localDeliveryType } = this.state;

    if (localDeliveryType !== DeliveryTypes.DELIVERY || isDeliveryCurrentlyUnavailable) {
      return false;
    } else if (!localAddress.formatted) {
      return false;
    } else {
      return true;
    }
  };

  shouldShowRestaurantInformation() {
    const { localAddress, localDeliveryType } = this.state;

    if (localDeliveryType === DeliveryTypes.TAKEOUT) {
      return true;
    } else if (!localAddress.formatted) {
      return false;
    } else {
      return true;
    }
  }

  shouldShowRestaurantAddress() {
    const { localDeliveryType } = this.state;

    return localDeliveryType === DeliveryTypes.TAKEOUT;
  }

  shouldShowAddressInput(isDeliveryCurrentlyUnavailable) {
    const { newAddressMode, localDeliveryType } = this.state;
    const { addresses } = this.props;
    const isNewCustomer = _.isEmpty(addresses) || _.every(addresses, (address) => !address.formatted);
    if (localDeliveryType !== DeliveryTypes.DELIVERY || isDeliveryCurrentlyUnavailable) {
      return false;
    } else if (isNewCustomer) {
      return true;
    } else if (newAddressMode) {
      return true;
    } else {
      return false;
    }
  }

  getTimeEstimationTitle() {
    const { t } = this.props;
    const { localDeliveryType } = this.state;

    if (localDeliveryType === DeliveryTypes.TAKEOUT) {
      return t('phoneordering_customerinfo_ordertype_address_pickup_by');
    } else {
      return t('phoneordering_customerinfo_ordertype_address_deliver_by');
    }
  }

  isExactAddress = (address) => {
    if (_.isEmpty(address)) {
      return false;
    }
    return !!address.street;
  };

  // we need different overflow behaviors : when the options menu is open, we want to allow overflow, and to hide it when its closed
  handleAddressInputChange = (ev) => {
    if (ev.target.value !== '') {
      this.setState({ contentOverflow: 'initial' });
    } else {
      this.setState({ contentOverflow: null });
    }
  };
  onChangeBranchClick = () => {
    const modal = Modals.BRANCH_SELECTOR;
    const { displayModal } = this.props;
    displayModal({ modal });
  };

  onDropdownEnterPressed = () => {
    if (!this.dropdownRef.current.isDropdownLayoutVisible()) {
      this.onFormSubmit();
    }
  };

  componentDidUpdate() {
    this.faderRef.current.isMounted && this.faderRef.current.updateHeight();
  }

  getPrimaryTitle(t, displayThumbnails, deliveryType) {
    if (displayThumbnails) {
      return t('phoneordering_customerinfo_ordertype_title');
    } else if (deliveryType === DeliveryTypes.TAKEOUT) {
      return t('phoneordering_customerinfo_pickuponly_title');
    } else if (deliveryType === DeliveryTypes.DELIVERY) {
      return t('phoneordering_customerinfo_deliveryonly_title');
    }
  }

  render() {
    const { localAddress, localDeliveryType } = this.state;
    const { organization, isModal, t } = this.props;
    const restaurantsName = organization.title[organization.locale];
    const displaySelectBranchButton = Boolean(organization.chainId);
    const deliveryOptions = RestaurantHelper.getDeliveryOptions({ restaurant: organization });
    const isDeliveryCurrentlyUnavailable = deliveryOptions.hasDelivery && !_.get(deliveryOptions, 'delivery.available');
    const supportedDeliveryTypes = [];

    if (deliveryOptions.hasDelivery) {
      supportedDeliveryTypes.push(DeliveryTypes.DELIVERY);
    }

    if (deliveryOptions.hasTakeout) {
      supportedDeliveryTypes.push(DeliveryTypes.TAKEOUT);
    }
    const displayThumbnails = !this.state.newAddressMode && supportedDeliveryTypes.length >= 2;
    const primaryTitle = this.getPrimaryTitle(t, displayThumbnails, localDeliveryType);
    const deliveryInfo = localAddress.latLng
      ? getDeliveryInfo({ address: localAddress, organization })
      : { type: 'delivery' };
    const bannerInfo = getBannerInfo({
      deliveryInfo,
      isExactAddress: this.isExactAddress(localAddress),
      organization,
      latLng: localAddress.latLng,
      t,
    });
    const disabled = localDeliveryType === DeliveryTypes.DELIVERY && bannerInfo.type === BANNER_TYPES.ERROR;
    const title = (
      <FaderTitle
        primary={primaryTitle}
        secondary={t('phoneordering_customerinfo_ordertype_address_addnew_popup_title')}
        drilldown={this.state.newAddressMode}
        displayBackButton
        onBackClick={() => {
          this.setState({ newAddressMode: false, localAddress: _.first(this.props.addresses) });
        }}
      />
    );

    const okText = isModal
      ? t('phoneordering_customerinfo_ordertype_address_addnew_popup_cta1')
      : t('phoneordering_customerinfo_ordertype_next_cta');
    const secondaryText = isModal
      ? t('phoneordering_customerinfo_ordertype_address_addnew_popup_cta2')
      : t('phoneordering_customerinfo_ordertype_back_cta');

    const formattedTimeGuarantee = getFormattedTimeGuarantee({ localDeliveryType, localAddress, t, organization });

    const footer = (
      <BasicModalLayoutFooter
        okText={okText}
        form
        cancelText={t('phoneordering_customerinfo_ordertype_address_addnew_popup_cta2')}
        onCancelClick={this.onRequestClose}
        displayCancelButton={Boolean(isModal)}
        displaySecondaryButton={!isModal}
        secondaryText={secondaryText}
        onSecondaryClick={this.onRequestClose}
        disabled={disabled}
        okAutoFocus={localDeliveryType === DeliveryTypes.TAKEOUT}
      />
    );

    const inner = (
      <Layout gap="18px">
        {displayThumbnails && (
          <Cell span={6}>
            <Thumbnail
              title={t('phoneordering_customerinfo_ordertype_delivery_label')}
              selected={localDeliveryType === DeliveryTypes.DELIVERY}
              dataHook={DataHooks.delivery}
              onClick={(e) => this.handleThumbnailClick(e, DeliveryTypes.DELIVERY)}
            />
          </Cell>
        )}

        {displayThumbnails && (
          <Cell span={6}>
            <Thumbnail
              title={t('phoneordering_customerinfo_ordertype_pickup_label')}
              selected={localDeliveryType === DeliveryTypes.TAKEOUT}
              dataHook={DataHooks.takeout}
              onClick={(e) => this.handleThumbnailClick(e, DeliveryTypes.TAKEOUT)}
            />
          </Cell>
        )}

        {this.shouldShowAddressInput(isDeliveryCurrentlyUnavailable) && (
          <Cell span={12}>
            <FormField
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_ordertype_address_label')}
                </Text>
              }
            >
              <AddressInput
                data-hook={DataHooks.addressInput}
                Client={GoogleMapsBasicClient}
                onChange={this.handleAddressInputChange}
                onSelect={this.onAddressInputSelect}
                placement={'bottom-start'}
                value={localAddress.formatted || ''}
                clientId="gme-wixcomltd2"
                flip={false}
                fixed={true}
                onManualInput={this.onManualInput}
                placeholder={t('phoneordering_customerinfo_ordertype_address_addnew_popup_placeholder')}
              />
            </FormField>
          </Cell>
        )}

        {/* TEMP!*/}
        {this.shouldShowAddressInput(isDeliveryCurrentlyUnavailable) &&
          !this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
            <div className={styles.placeholder} data-hook="placeholder" />
          )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) &&
          !this.shouldShowAddressInput(isDeliveryCurrentlyUnavailable) && (
            <Cell span={12}>
              <FormField
                label={
                  <Text size="medium" secondary>
                    {t('phoneordering_customerinfo_ordertype_address_label')}
                  </Text>
                }
              >
                <div onScroll={(e) => e.stopPropagation()}>
                  <Dropdown
                    dataHook={DataHooks.dropdown}
                    options={this.getAddressOptions()}
                    selectedId={localAddress.id}
                    onSelect={this.onAddressDropdownSelect}
                    onEnterPressed={this.onDropdownEnterPressed}
                    ref={this.dropdownRef}
                    autoFocus
                  />
                </div>
              </FormField>
            </Cell>
          )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
          <Cell span={12}>
            <AddressInformationBanner
              t={t}
              organization={organization}
              bannerInfo={bannerInfo}
              deliveryInfo={deliveryInfo}
            />
          </Cell>
        )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
          <Cell span={4}>
            <FormField
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_ordertype_delivery_apt_label')}
                </Text>
              }
            >
              <Input {...this.getInputProps('apt')} />
            </FormField>
          </Cell>
        )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
          <Cell span={4}>
            <FormField
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_ordertype_delivery_floor_label')}
                </Text>
              }
            >
              <Input {...this.getInputProps('floor')} />
            </FormField>
          </Cell>
        )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
          <Cell span={4}>
            <FormField
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_ordertype_delivery_entrance_label')}
                </Text>
              }
            >
              <Input {...this.getInputProps('entrance')} />
            </FormField>
          </Cell>
        )}

        {this.shouldShowFurtherInputs(isDeliveryCurrentlyUnavailable) && (
          <Cell span={12}>
            <FormField
              label={
                <Text size="medium" secondary>
                  {t('phoneordering_customerinfo_ordertype_delivery_instructions_label')}
                </Text>
              }
            >
              <InputArea
                placeholder={t('phoneordering_customerinfo_ordertype_delivery_instructions_placeholder')}
                {...this.getInputProps('comment')}
              />
            </FormField>
          </Cell>
        )}

        {this.shouldShowRestaurantInformation(isDeliveryCurrentlyUnavailable) && (
          <Cell span={12}>
            <div className={styles.metadata} data-hook={DataHooks.restaurant}>
              {this.shouldShowRestaurantAddress(isDeliveryCurrentlyUnavailable) && (
                <React.Fragment>
                  <div className={styles.restaurant}>
                    <div>
                      <Text size="medium" secondary>
                        {t('phoneordering_customerinfo_ordertype_address_pickup_from')}
                      </Text>
                      <div className={styles.labelSpacer} />
                      <Text size="medium" weight="normal" tagName="div" data-hook={DataHooks.restaurantAddress}>
                        {organization.address.formatted}
                      </Text>
                    </div>
                    {displaySelectBranchButton && (
                      <TextButton dataHook={DataHooks.selectBranch} onClick={this.onChangeBranchClick}>
                        {t('phoneordering_customerinfo_ordertype_delivery_branch_cta')}
                      </TextButton>
                    )}
                  </div>
                  <div className={styles.divider} />
                </React.Fragment>
              )}
              <Text size="medium" secondary>
                {this.getTimeEstimationTitle()}
              </Text>
              <div className={styles.labelSpacer} />
              <Text size="medium" weight="normal" tagName="div" data-hook={DataHooks.deliveryTime}>
                {isDeliveryCurrentlyUnavailable && this.state.localDeliveryType === DeliveryTypes.DELIVERY
                  ? 'Unavailable'
                  : formattedTimeGuarantee}
              </Text>
            </div>
          </Cell>
        )}
      </Layout>
    );

    const prefix = <OnBoardingStepper />;

    const renderedContent = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          this.onFormSubmit();
        }}
        data-hook="address-info-form"
      >
        <ModalLayout
          card={!isModal}
          title={title}
          contentOverflow={this.state.contentOverflow}
          contentPadding="9px 30px 12px 30px"
          footer={footer}
          onCloseClick={this.onRequestClose}
          scrollable
          prefix={!isModal && prefix}
        >
          <div data-hook="address-info-wrapper">
            <Fader
              ref={this.faderRef}
              primary={inner}
              secondary={inner}
              drilldown={this.state.newAddressMode}
              delayedEnter
              dynamicHeight
            />
          </div>
        </ModalLayout>
      </form>
    );

    return this.wrapIfNeeded(renderedContent);
  }
}

const getAddressOutOfRangeText = ({ organization, t }) => {
  const rawDeliveryFee = getMostExpensiveDeliveryFee(organization);
  const maxDeliveryCharge = CurrencyUtils.toStr(rawDeliveryFee / 100, organization.currency, organization.locale);
  return t('phoneordering_customerinfo_ordertype_deliverycharge_outarea', { maxDeliveryCharge });
};

const getBannerInfoForApproximateAllowed = ({ isExactAddress, latLng, isInDeliveryArea, organization, t }) => {
  const isCantPinPointError = !latLng || !isExactAddress;
  const resolveOutsideDeliveryArea = () => {
    const outsideDeliveryArea = {
      type: BANNER_TYPES.INFO,
      text: getAddressOutOfRangeText({ organization, t }),
    };
    const outsideAreaAndPinpointError = {
      type: BANNER_TYPES.INFO,
      mainText: getAddressOutOfRangeText({ organization, t }),
      text: 'In addition, we can’t pinpoint the exact entrance for this address.',
    };
    return isCantPinPointError ? outsideAreaAndPinpointError : outsideDeliveryArea;
  };
  const resolveInsideDeliveryArea = () => {
    const notExactAddress = {
      type: BANNER_TYPES.INFO,
      text: 'Note, we can’t pinpoint the exact entrance for this address.',
    };
    const goodBanner = { type: BANNER_TYPES.SUCCESS };
    return isExactAddress ? goodBanner : notExactAddress;
  };

  return isInDeliveryArea ? resolveInsideDeliveryArea() : resolveOutsideDeliveryArea();
};

const getBannerInfoForApproximateDisallowed = ({ isExactAddress, latLng, isInDeliveryArea, organization, t }) => {
  const isCantPinPointError = !latLng || !isExactAddress;
  const pinpointbanner = {
    type: BANNER_TYPES.ERROR,
    text: t('phoneordering_customerinfo_ordertype_delivery_address_notfound'),
  };
  const resolveExactAddress = () => {
    const goodBanner = { type: BANNER_TYPES.SUCCESS };
    const outsideDeliverArea = {
      type: BANNER_TYPES.STANDARD,
      text: getAddressOutOfRangeText({ organization, t }),
    };
    return isInDeliveryArea ? goodBanner : outsideDeliverArea;
  };
  return isCantPinPointError ? pinpointbanner : resolveExactAddress();
};

const getBannerInfo = ({ deliveryInfo, isExactAddress, organization, latLng, t }) => {
  const isInDeliveryArea =
    !_.isEmpty(deliveryInfo) &&
    deliveryInfo.type === 'delivery' &&
    !Boolean(_.get(deliveryInfo, 'availability.exceptions', false));
  if (!organization || !organization.deliveriesInfo) {
    // for tests
    return;
  }
  const allowsApproximateAddress =
    organization.deliveriesInfo.approximateAddresses && organization.deliveriesInfo.approximateAddresses === 'allow';

  return allowsApproximateAddress
    ? getBannerInfoForApproximateAllowed({ deliveryInfo, isInDeliveryArea, isExactAddress, latLng, organization, t })
    : getBannerInfoForApproximateDisallowed({
        deliveryInfo,
        isInDeliveryArea,
        isExactAddress,
        latLng,
        organization,
        t,
      });
};

AddressInformation.displayName = 'AddressInformation';
AddressInformation.propTypes = {
  organization: PropTypes.object,
  deliveryType: PropTypes.oneOf(['takeout', 'delivery']),
  isModal: PropTypes.bool,
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  goBack: PropTypes.func,
  updateDeliveryAddress: PropTypes.func,
  deliveryDetailsDone: PropTypes.func,
  updateDeliveryType: PropTypes.func,
  supportedDeliveryTypes: PropTypes.array,
  displayModal: PropTypes.func,
  hideModal: PropTypes.func,
  t: PropTypes.func,
};
